// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import type {IARContext} from '@app/features/devtools/components/modals/IARModal';
import {IARModal} from '@app/features/devtools/components/modals/IARModal';
import type {Guild} from '@app/features/guild/models/Guild';
import type {Message} from '@app/features/messaging/models/Message';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {User} from '@app/features/user/models/User';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

function openReportModal(context: IARContext): void {
	ModalCommands.push(modal(() => <IARModal context={context} />));
}
function openFollowupReportModal(context: IARContext): void {
	globalThis.setTimeout(() => {
		openReportModal(context);
	}, 0);
}
export function openReportMessageModal(message: Message): void {
	openReportModal({
		type: 'message',
		message,
	});
}
export function openReportUserModal(params: {i18n: I18n; user: User; guildId?: string; message?: Message}): void {
	const {i18n, user, guildId, message} = params;
	const context: IARContext = {
		type: 'user',
		user,
		guildId,
	};
	const description = message
		? i18n._(
				msg`If this report is about a specific message, report that message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the user as a whole if that message does not capture the broader issue.`,
			)
		: i18n._(
				msg`If this report is about specific messages, report the most relevant message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the user as a whole if reporting a message would not capture the broader issue.`,
			);
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={i18n._(msg`Report User`)}
				description={description}
				message={message}
				primaryText={i18n._(msg`Continue to Report User`)}
				primaryVariant="danger-primary"
				onPrimary={() => openFollowupReportModal(context)}
			/>
		)),
	);
}
export function openReportGuildModal(params: {i18n: I18n; guild: Guild}): void {
	const {i18n, guild} = params;
	const context: IARContext = {
		type: 'guild',
		guild,
	};
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={i18n._(msg`Report Community`)}
				description={i18n._(
					msg`If this report is about a specific message in this community, report that message instead. Message reports give our Safety Team the clearest context, and adding details in the comments can help us review it faster. Only continue with reporting the community as a whole if reporting a message would not capture the broader issue.`,
				)}
				primaryText={i18n._(msg`Continue to Report Community`)}
				primaryVariant="danger-primary"
				onPrimary={() => openFollowupReportModal(context)}
			/>
		)),
	);
}
